// Form builder > Logic tab > Simple trigger: when "When the form component" is
// groupName or roleName, replace the free-text "Has the value" box with a dropdown
// of the real names. Any other component keeps the text box.
//
// Formio defines the Logic tab as a shared array, so this patches it once on import.
import axios from "axios";
import logicTab from "formiojs/components/_classes/component/editForm/Component.edit.logic";

// Form field key -> where its allowed values come from.
const VALUE_SOURCES = {
  groupName: { endpoint: "/ems/groups", nameField: "group_name" },
  roleName: { endpoint: "/ems/roles", nameField: "role_name" },
  invitationTypeName: { endpoint: "/ems/invitation-types", nameField: "name" },
  accreditationName: { endpoint: "/ems/accreditation-categories", nameField: "category_name" },
};

const namesByField = {};
const loading = {};

const loadNames = async (fieldKey) => {
  const source = VALUE_SOURCES[fieldKey];
  if (!source || loading[fieldKey] || namesByField[fieldKey]?.length) return;
  loading[fieldKey] = true;
  try {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}${source.endpoint}`);
    if (Array.isArray(res.data)) {
      namesByField[fieldKey] = res.data.map((item) => item[source.nameField]).filter(Boolean);
    }
  } catch (err) {
    console.error(`Failed to load ${fieldKey} values for the form builder`, err);
  } finally {
    loading[fieldKey] = false;
  }
};

export const loadSuggestionValues = () => Promise.all(Object.keys(VALUE_SOURCES).map(loadNames));

const hasSuggestions = ({ row }) => Boolean(VALUE_SOURCES[row?.when]);

// The "simple" block is nested (triggerPanel > trigger > simple), so search the tree.
const findByKey = (components = [], key) => {
  for (const c of components) {
    if (c.key === key) return c;
    const found = findByKey(c.components, key);
    if (found) return found;
  }
  return null;
};

const patchLogicTab = () => {
  const simple = findByKey(logicTab, "simple");
  const eqIndex = simple?.components?.findIndex((c) => c.key === "eq");
  if (eqIndex === undefined || eqIndex < 0 || simple.patchedForSuggestions) return;

  const textEq = simple.components[eqIndex];
  textEq.clearOnHide = false;
  textEq.customConditional = (ctx) => !hasSuggestions(ctx);

  const dropdownEq = {
    type: "select",
    input: true,
    label: "Has the value:",
    key: "eq",
    tableView: false,
    clearOnHide: false,
    dataSrc: "custom",
    valueProperty: "value",
    template: "<span>{{ item.label }}</span>",
    customConditional: hasSuggestions,
    data: {
      custom: (context) => {
        const fieldKey = context?.row?.when;
        loadNames(fieldKey);
        return (namesByField[fieldKey] || []).map((name) => ({ label: name, value: name }));
      },
    },
  };

  simple.components.splice(eqIndex + 1, 0, dropdownEq);
  simple.patchedForSuggestions = true;
};

patchLogicTab();
